import { Suspense } from "react";
/**
 * Today: what needs me now. A one-line brief, attention grouped by urgency, active jobs and what
 * changed. Calm when there is nothing to do, and every empty state teaches one thing to say.
 */
import { Link } from "react-router";
import type { TaskDTO, TodayDTO, TodayItem } from "../../../contract";
import { formatDate, relativeDay } from "../../lib/format";
import { t, useLanguage } from "../../lib/i18n";
import { useApi } from "../../lib/query";
import { useCan, useMe } from "../../lib/session";
import { webModules } from "../../registry";
import { Spinner, Button, Card, ErrorNote, Icon, List, Loading, Progress, Row, Section, useFromHere, useIsWide } from "../../ui/kit";
import { useOpenComposer } from "../Composer";
import { Page, usePins } from "../Layout";
import { ProposalCard } from "../ProposalCard";
import { EventRow } from "../Timeline";
import { groupToday, morningBrief, type Urgency } from "../today";
import { TaskRow } from "./Work";

function greeting(name: string): string {
  const h = new Date().getHours();
  const first = name.split(" ")[0];
  if (h < 12) return t("Good morning, {{name}}", { name: first });
  if (h < 18) return t("Good afternoon, {{name}}", { name: first });
  return t("Good evening, {{name}}", { name: first });
}

/** Marks an English catalog key (collected by the i18n check); translated with t() where shown. */
const tr = (english: string) => english;
const GROUP_TITLES: Record<Urgency, string> = { now: tr("Needs you now"), today: tr("Today"), soon: tr("This week"), later: tr("Coming up") };

export function Today() {
  useLanguage();
  const { data: me } = useMe();
  const can = useCan();
  const wide = useIsWide();
  const { data, isLoading, error, refetch } = useApi<TodayDTO>("/today", { refetchInterval: 60_000 });
  const openComposer = useOpenComposer();
  const { pins } = usePins();
  const from = useFromHere(t("Today"));
  if (isLoading) return <Loading />;
  if (error || !data) return <Page><ErrorNote error={error} retry={() => refetch()} /></Page>;
  const groups = groupToday(data);
  const brief = morningBrief(data);
  const waiting = data.review.latest.filter((c) => c.status === "needs_review");
  const attention = (["now", "today", "soon", "later"] as const).filter((g) => groups[g].items.length || groups[g].tasks.length);
  const nothingDue = !waiting.length && !attention.length;
  const empty = data.counts.animals === 0;

  return (
    <Page>
      <header className="today-head">
        <div className="today-hello">
          <p className="eyebrow">{formatDate(data.date, "date", { weekday: true })}</p>
          <h1>{greeting(me?.person.name ?? "")}</h1>
          <p className="brief" aria-label={t("Today in short")}>
            {brief.length ? brief.map((part, i) => <span key={i}>{part}</span>) : <span>{empty ? t("Let's get your ranch set up.") : t("Nothing pressing today.")}</span>}
          </p>
        </div>
        <div className="today-stats">
          <Link to="/cattle" className="stat stat-link"><span className="stat-value">{data.counts.animals}</span><span className="stat-label">{t("Head")}</span></Link>
          <Link to="/review" className={`stat stat-link${data.review.count ? " tone-warn" : ""}`}><span className="stat-value">{data.review.count}</span><span className="stat-label">{t("Notes to check")}</span></Link>
        </div>
      </header>

      {empty && (
        <Card className="welcome">
          <h2>{t("Let's get your cattle in")}</h2>
          <p>{t("Bring in a spreadsheet or old records, or just start talking: “Cow 42, red, born 2019.”")}</p>
          <div className="row-actions">
            <Button variant="primary" icon="upload" to="/ranch/data">{t("Bring in records")}</Button>
            {can("record") && <Button icon="mic" onClick={() => openComposer({ mic: true })}>{t("Say it instead")}</Button>}
          </div>
        </Card>
      )}

      {waiting.length > 0 && (
        <Section title={<><Icon name="inbox" size={20} />{t("Notes to check")}</>} count={data.review.count} className="today-review"
          action={data.review.count > 2 ? <Link to="/review" className="link">{t("See all {{count}}", { count: data.review.count })}</Link> : undefined}>
          <div className="stack">{waiting.slice(0, 2).map((c) => <ProposalCard key={c.id} capture={c} compact />)}</div>
        </Section>
      )}

      {attention.map((g) => (
        <Section key={g} title={t(GROUP_TITLES[g])} count={groups[g].items.length + groups[g].tasks.length} className={`today-group today-${g}`}>
          <List>
            {groups[g].items.map((item) => <AttentionRow key={item.id} item={item} date={data.date} from={from} />)}
            {groups[g].tasks.map((task: TaskDTO) => <TaskRow key={task.id} task={task} today={data.date} />)}
          </List>
        </Section>
      ))}

      {nothingDue && !empty && (
        <Card className="caught-up">
          <span className="empty-icon"><Icon name="done" size={24} /></span>
          <div>
            <p className="empty-title">{t("All caught up")}</p>
            <p className="muted">{t("When something happens, just say it. For example: “The gate in the South pasture needs fixing.” Hondo makes it a task.")}</p>
          </div>
          {can("record") && <Button icon="mic" onClick={() => openComposer({ draft: t("The gate in the South pasture needs fixing") })}>{t("Try it")}</Button>}
        </Card>
      )}

      {!wide && pins.length > 0 && (
        <Section title={t("Pinned")}>
          <div className="pin-chips">{pins.map((p) => <Link key={p.id} to={p.to} className="pin-chip"><Icon name={p.icon} size={16} />{t(p.label)}</Link>)}</div>
        </Section>
      )}

      {data.jobs.length > 0 && (
        <Section title={t("In progress")} count={data.jobs.length} action={<Link to="/work" className="link">{t("All work")}</Link>}>
          <div className="cards">
            {data.jobs.slice(0, 6).map((j) => (
              <Card key={j.id} to={`/work/jobs/${j.id}`} className="job-card">
                <span className="job-title">{j.title}</span>
                {j.progress.total > 0 && <Progress done={j.progress.done} total={j.progress.total} exceptions={j.progress.exceptions} label={j.title} />}
                {j.due_at && <span className="job-due"><Icon name="calendar" size={14} />{relativeDay(j.due_at, data.date)}</span>}
              </Card>
            ))}
          </div>
        </Section>
      )}

      {webModules.flatMap((m) => m.todayCards ?? []).map((C, i) => <Suspense key={i} fallback={null}><C /></Suspense>)}

      <Section title={t("Recently")} action={<Link to="/history" className="link">{t("Ranch log")}</Link>}>
        {data.recent.length > 0 ? <ol className="timeline-list">{data.recent.slice(0, 8).map((e) => <EventRow key={e.id} event={e} />)}</ol> : (
          <p className="muted teach">{t("Nothing recorded yet. Say “Moved the heifers to the Creek trap” and it shows up here.")}</p>
        )}
      </Section>
    </Page>
  );
}

function AttentionRow({ item, date, from }: { item: TodayItem; date: string; from: { from: { to: string; label: string } } }) {
  const due = item.due_at ? relativeDay(item.due_at, date) : null;
  const overdue = item.due_at && item.due_at.slice(0, 10) < date;
  return (
    <Row icon={item.icon} tone={item.tone} to={item.link} state={from}
      title={t(item.title.text, item.title.values)}
      meta={<>{item.detail ? t(item.detail.text, item.detail.values) : null}{item.detail && due ? " · " : null}{due && <span className={overdue ? "is-overdue" : undefined}>{overdue ? t("Overdue: {{when}}", { when: due }) : due}</span>}</>}
      trailing={item.kind === "alert" ? <span className="row-flag tone-bad"><Icon name="warn" size={16} /><span className="sr-only">{t("Alert")}</span></span> : undefined} />
  );
}
